import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, formatTime } from '../../lib/vdot';

export function Athletes() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const isAdmin = profile?.is_admin || profile?.role === 'admin';

  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaused, setShowPaused] = useState(false);
  const [filterCoachId, setFilterCoachId] = useState('');
  const [filterLevel, setFilterLevel] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCoachId, setInviteCoachId] = useState('');
  const [inviteStatus, setInviteStatus] = useState(''); // '' | 'searching' | 'success' | 'error'
  const [inviteMessage, setInviteMessage] = useState('');
  const [coaches, setCoaches] = useState([]);

  useEffect(() => {
    if (profile) {
      fetchAthletes();
      if (isAdmin) fetchCoaches();
    }
  }, [profile]);

  async function fetchCoaches() {
    const { data: archivedCoachProfiles } = await supabase.from('coach_profiles').select('id').eq('archived', true);
    const archivedIds = new Set((archivedCoachProfiles ?? []).map(c => c.id));
    const { data } = await supabase.from('profiles').select('id, full_name').eq('role', 'coach').order('full_name');
    setCoaches((data ?? []).filter(c => !archivedIds.has(c.id)));
  }

  async function fetchAthletes() {
    setLoading(true);

    let apQuery = supabase.from('athlete_profiles').select('id, grade_year, grade_level, events, coach_id, paused, archived');
    if (!isAdmin) apQuery = apQuery.eq('coach_id', profile.id);
    const { data: athleteProfiles } = await apQuery;

    if (!athleteProfiles || athleteProfiles.length === 0) {
      setAthletes([]);
      setLoading(false);
      return;
    }

    const ids = athleteProfiles.map(a => a.id);

    const [{ data: profileNames }, { data: benchmarks }] = await Promise.all([
      supabase.from('profiles').select('id, full_name, email, role').in('id', ids).order('full_name'),
      supabase.from('benchmarks').select('athlete_id, event, time_seconds, race_date')
        .in('athlete_id', ids).order('race_date', { ascending: false }),
    ]);

    // Admin: fetch coach names
    let coachNameMap = {};
    if (isAdmin) {
      const coachIds = [...new Set(athleteProfiles.map(ap => ap.coach_id).filter(Boolean))];
      if (coachIds.length) {
        const { data: coachProfiles } = await supabase.from('profiles').select('id, full_name').in('id', coachIds);
        coachNameMap = Object.fromEntries((coachProfiles ?? []).map(c => [c.id, c.full_name]));
      }
    }

    const merged = (profileNames ?? []).map(p => {
      const ap = athleteProfiles.find(a => a.id === p.id);
      const latestBenchmark = benchmarks?.find(b => b.athlete_id === p.id);
      let vdot = null;
      if (latestBenchmark) {
        const result = calculatePaces(latestBenchmark.event, latestBenchmark.time_seconds);
        vdot = result?.vdot ?? null;
      }
      return {
        id: p.id,
        full_name: p.full_name ?? '—',
        email: p.email ?? '',
        grade_year: ap?.grade_year,
        grade_level: ap?.grade_level ?? null,
        events: ap?.events ?? [],
        latestBenchmark,
        vdot,
        coachId: ap?.coach_id ?? null,
        coachName: isAdmin ? (ap?.coach_id ? (coachNameMap[ap.coach_id] ?? 'Unknown') : null) : undefined,
        paused: (ap?.paused ?? false) || (ap?.archived ?? false),
        isPro: p.role === 'pro_athlete' || ap?.grade_level === 'pro',
      };
    });

    setAthletes(merged);
    setLoading(false);
  }

  async function handleAddAthlete(e) {
    e.preventDefault();
    setInviteStatus('searching');
    setInviteMessage('');

    const { data: found, error } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .eq('email', inviteEmail.trim().toLowerCase())
      .in('role', ['athlete', 'pro_athlete'])
      .maybeSingle();

    if (error || !found) {
      setInviteStatus('error');
      setInviteMessage("No athlete account found with that email. Make sure they've signed up with the Athlete or Pro Athlete role.");
      return;
    }

    const assignCoachId = isAdmin ? (inviteCoachId || null) : profile.id;

    const { data: existing } = await supabase
      .from('athlete_profiles')
      .select('id, joined_apex_date, billing_start_month, paused')
      .eq('id', found.id)
      .maybeSingle();

    // If athlete is paused, reactivate them with a fresh joined date
    if (existing?.paused) {
      const todayIso = new Date().toISOString().slice(0, 10);
      const { error: upsertError } = await supabase
        .from('athlete_profiles')
        .upsert({
          id: found.id,
          coach_id: assignCoachId,
          paused: false,
          joined_apex_date: todayIso,
          billing_start_month: null,
          billing_start_year: null,
        });
      if (upsertError) {
        setInviteStatus('error');
        setInviteMessage('Something went wrong. Please try again.');
        return;
      }
      setInviteStatus('success');
      setInviteMessage(`${found.full_name} has been reactivated on your roster.`);
      setInviteEmail('');
      await fetchAthletes();
      return;
    }

    if (existing && !isAdmin) {
      setInviteStatus('error');
      setInviteMessage(`${found.full_name} is already on your roster.`);
      return;
    }

    const todayIso = new Date().toISOString().slice(0, 10);
    const upsertData = { id: found.id, coach_id: assignCoachId };
    // Stamp joined date only when first adding (no existing row, or
    // no date set yet — checks both new and legacy columns).
    if (!existing || (!existing.joined_apex_date && !existing.billing_start_month)) {
      upsertData.joined_apex_date = todayIso;
    }

    const { error: upsertError } = await supabase
      .from('athlete_profiles')
      .upsert(upsertData);

    if (upsertError) {
      setInviteStatus('error');
      setInviteMessage('Something went wrong. Please try again.');
      return;
    }

    const coachLabel = isAdmin
      ? (coaches.find(c => c.id === assignCoachId)?.full_name ?? 'Unassigned')
      : 'your roster';
    setInviteStatus('success');
    setInviteMessage(`${found.full_name} has been added${isAdmin ? ` and assigned to ${coachLabel}` : ' to your roster'}.`);
    setInviteEmail('');
    await fetchAthletes();
  }

  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <p className="page-eyebrow">Coaching</p>
          <h1 className="page-title">Athletes</h1>
        </div>
        <button
          className="btn btn-primary"
          style={{ width: 'auto', padding: '10px 20px' }}
          onClick={() => setShowAddForm(v => !v)}
        >
          {showAddForm ? 'Cancel' : '+ Add Athlete'}
        </button>
      </div>

      {showAddForm && (
        <div className="section-card" style={{ marginBottom: 24 }}>
          <div className="section-card-header">
            <span className="section-card-title">Add Athlete by Email</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <form onSubmit={handleAddAthlete} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
                <label className="form-label">Athlete's Email Address</label>
                <input
                  className="form-input"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="athlete@email.com"
                  required
                />
              </div>
              {isAdmin && (
                <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label className="form-label">Assign to Coach</label>
                  <select
                    className="form-input form-select"
                    value={inviteCoachId}
                    onChange={e => setInviteCoachId(e.target.value)}
                  >
                    <option value="">— Unassigned —</option>
                    {coaches.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                  </select>
                </div>
              )}
              <button
                className="btn btn-primary"
                type="submit"
                style={{ width: 'auto', padding: '10px 20px' }}
                disabled={inviteStatus === 'searching'}
              >
                {inviteStatus === 'searching' ? 'Searching...' : 'Add Athlete'}
              </button>
            </form>
            {inviteMessage && (
              <p style={{
                marginTop: 12,
                fontSize: 13,
                color: inviteStatus === 'success' ? 'var(--green, #16a34a)' : 'var(--red, #dc2626)',
              }}>
                {inviteMessage}
              </p>
            )}
            <p style={{ marginTop: 12, fontSize: 12, color: 'var(--gray-400)' }}>
              The athlete must have already created an account at this app using the Athlete role.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      ) : athletes.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">No athletes yet</p>
          <p className="empty-state-sub">
            Click "+ Add Athlete" above and enter their email address. They must have already signed up with the Athlete role.
          </p>
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          {athletes.some(a => a.paused) && (
            <button
              className={`btn-xs ${showPaused ? 'btn-xs-primary' : 'btn-xs-ghost'}`}
              onClick={() => setShowPaused(s => !s)}
            >
              {showPaused ? 'Showing All' : 'Active Only'} ({athletes.filter(a => a.paused).length} paused)
            </button>
          )}
          {isAdmin && (
            <select
              className="form-input form-select"
              style={{ width: 'auto', minWidth: 180, fontSize: 13, padding: '6px 10px', height: 'auto' }}
              value={filterCoachId}
              onChange={e => setFilterCoachId(e.target.value)}
            >
              <option value="">All Coaches</option>
              <option value="__unassigned__">Unassigned</option>
              {coaches.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          )}
          {isAdmin && (
            <select
              className="form-input form-select"
              style={{ width: 'auto', minWidth: 160, fontSize: 13, padding: '6px 10px', height: 'auto' }}
              value={filterLevel}
              onChange={e => setFilterLevel(e.target.value)}
            >
              <option value="">All Levels</option>
              <option value="middle">Middle School</option>
              <option value="high">High School</option>
              <option value="collegiate">Collegiate</option>
              <option value="post-collegiate">Post-Collegiate</option>
              <option value="pro">Pro</option>
            </select>
          )}
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Athlete</th>
                {isAdmin && <th>Coach</th>}
                <th>Events</th>
                <th>Grade / Year</th>
                <th>Latest Benchmark</th>
                <th>VDOT</th>
              </tr>
            </thead>
            <tbody>
              {athletes.filter(a => {
                if (!showPaused && a.paused) return false;
                if (filterCoachId === '__unassigned__' && a.coachId) return false;
                if (filterCoachId && filterCoachId !== '__unassigned__' && a.coachId !== filterCoachId) return false;
                if (filterLevel) {
                  const gl = a.grade_level;
                  if (filterLevel === 'middle' && !['6th','7th','8th'].includes(gl)) return false;
                  if (filterLevel === 'high' && !['9th','10th','11th','12th'].includes(gl)) return false;
                  if (filterLevel === 'collegiate' && gl !== 'collegiate') return false;
                  if (filterLevel === 'post-collegiate' && gl !== 'post-collegiate') return false;
                  if (filterLevel === 'pro' && !a.isPro) return false;
                }
                return true;
              }).map(athlete => (
                <tr
                  key={athlete.id}
                  className="clickable"
                  style={athlete.paused ? { opacity: 0.5 } : undefined}
                  onClick={() => navigate(`/coach/athletes/${athlete.id}`)}
                >
                  <td>
                    <div className="cell-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {athlete.full_name}
                      {athlete.paused && (
                        <span style={{ fontSize: 10, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Paused</span>
                      )}
                    </div>
                    <div className="cell-muted">{athlete.email}</div>
                  </td>
                  {isAdmin && (
                    <td>
                      {athlete.coachName || <span className="cell-muted">Unassigned</span>}
                    </td>
                  )}
                  <td>
                    {athlete.events.length > 0
                      ? athlete.events.map(e => EVENTS[e]?.label ?? e).join(', ')
                      : <span className="cell-muted">—</span>}
                  </td>
                  <td>
                    {athlete.grade_year || <span className="cell-muted">—</span>}
                  </td>
                  <td>
                    {athlete.latestBenchmark ? (
                      <div>
                        <div>{EVENTS[athlete.latestBenchmark.event]?.label ?? athlete.latestBenchmark.event}</div>
                        <div className="cell-muted">{formatTime(athlete.latestBenchmark.time_seconds)}</div>
                      </div>
                    ) : <span className="cell-muted">No benchmarks yet</span>}
                  </td>
                  <td>
                    {athlete.vdot ? (
                      <span className="vdot-badge">VDOT <span>{athlete.vdot}</span></span>
                    ) : <span className="cell-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </Layout>
  );
}
